import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import CollateralCard, { getAvgReadPct } from './CollateralCard';
import type { CollateralData } from '../../api/client';
import PlaybooksPanel from './PlaybooksPanel';

type LibTab = 'collaterals' | 'playbooks';
type TypeFilter = 'all' | 'pdf' | 'ppt' | 'video' | 'image' | 'xls';
type ReadFilter = '' | 'unread' | 'partial' | 'read';

export default function LibraryScreen() {
  const { colls, playbooks, toast, go, setActivePbIdx } = useApp();
  const [tab, setTab] = useState<LibTab>('collaterals');
  const [sel, setSel] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [readFilter, setReadFilter] = useState<ReadFilter>('');
  const [sortBy, setSortBy] = useState('default');
  const [senderFilter, setSenderFilter] = useState('');
  const [previewModal, setPreviewModal] = useState<{ coll: CollateralData; page: number } | null>(null);

  const filtered = useMemo(() => {
    let items = [...colls];
    if (typeFilter !== 'all') items = items.filter(c => c.type === typeFilter);
    if (senderFilter) items = items.filter(c => c.sharedBy === senderFilter);
    if (search) items = items.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.tags.some(t => t.toLowerCase().includes(search.toLowerCase())));
    if (readFilter) {
      items = items.filter(c => {
        const avg = getAvgReadPct(c);
        if (readFilter === 'unread') return avg === 0;
        if (readFilter === 'partial') return avg > 0 && avg < 100;
        if (readFilter === 'read') return avg === 100;
        return true;
      });
    }
    if (sortBy === 'name') items.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'read-desc') items.sort((a, b) => getAvgReadPct(b) - getAvgReadPct(a));
    return items;
  }, [colls, typeFilter, senderFilter, search, readFilter, sortBy]);

  const toggleCard = (id: number | string) => { const sid = String(id); setSel(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]); };

  const selectStyle: React.CSSProperties = { padding: '7px 28px 7px 10px', borderRadius: '8px', border: '1.5px solid var(--border2)', fontFamily: 'inherit', fontSize: '12px', background: 'white', cursor: 'pointer', outline: 'none', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23718096' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', maxWidth: '960px', margin: '0 auto', width: '100%' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--navy)' }}>Collateral Library</div>
            <div style={{ fontSize: '12px', color: 'var(--text3)' }}>Select collaterals · create a Sales Playbook</div>
          </div>
          <button onClick={() => toast('Upload feature coming soon!')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: '1.5px solid var(--border2)', background: 'white', color: 'var(--text2)', fontFamily: 'inherit', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
            <i className="ti ti-upload" /> Upload
          </button>
        </div>

        {/* Selection bar */}
        {sel.length > 0 && (
          <div style={{ background: 'var(--navy)', color: 'white', padding: '9px 18px', display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '8px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', flex: 1 }}>
              {sel.map(id => { const c = colls.find(x => (x._id || String(x.id)) === String(id))!; return (
                <div key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,.12)', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '500', maxWidth: '180px' }}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name.substring(0, 20)}{c.name.length > 20 ? '…' : ''}</span>
                  <button onClick={() => toggleCard(id)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.6)', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '0 0 0 3px' }}>×</button>
                </div>
              ); })}
            </div>
            <button onClick={() => setSel([])} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 11px', borderRadius: '8px', background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.2)', color: 'white', fontFamily: 'inherit', fontSize: '12px', cursor: 'pointer', flexShrink: 0 }}>
              <i className="ti ti-x" /> Clear
            </button>
            <button onClick={() => toast('Quick Share sent!')} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 11px', borderRadius: '8px', background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.25)', color: 'white', fontFamily: 'inherit', fontSize: '12px', cursor: 'pointer', flexShrink: 0 }}>
              <i className="ti ti-bolt" style={{ color: '#FCD34D' }} /> Quick Share
            </button>
            <button onClick={() => { toast('Opening playbook wizard…'); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 14px', borderRadius: '8px', background: '#C8973A', border: '1px solid #C8973A', color: 'white', fontFamily: 'inherit', fontSize: '12px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
              <i className="ti ti-book-2" /> Build Playbook
            </button>
          </div>
        )}

        {/* Sub nav tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '0' }}>
          {(['collaterals', 'playbooks'] as LibTab[]).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 16px', border: 'none', background: 'none', fontFamily: 'inherit', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: tab === t ? 'var(--navy)' : 'var(--text3)', borderBottom: tab === t ? '2px solid var(--gold)' : '2px solid transparent', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'capitalize' }}>
              <i className={`ti ${t === 'collaterals' ? 'ti-files' : 'ti-book-2'}`} />
              {t.charAt(0).toUpperCase() + t.slice(1)}
              {t === 'playbooks' && <span style={{ background: '#EDF2F7', color: '#4A5568', fontSize: '10px', fontWeight: '700', borderRadius: '20px', padding: '1px 8px' }}>{playbooks.length}</span>}
            </button>
          ))}
        </div>

        {/* Collaterals panel */}
        {tab === 'collaterals' && (
          <div style={{ paddingTop: '16px' }}>
            {/* Filters */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: '180px' }}>
                  <i className="ti ti-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: 'var(--text3)', pointerEvents: 'none' }} />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search collaterals…" style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: '8px', border: '1.5px solid var(--border2)', fontFamily: 'inherit', fontSize: '12px', outline: 'none', background: 'white', boxSizing: 'border-box' }} />
                </div>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...selectStyle, minWidth: '130px' }}>
                  <option value="default">Default order</option>
                  <option value="name">Name A–Z</option>
                  <option value="read-desc">Most read</option>
                </select>
                <select value={senderFilter} onChange={e => setSenderFilter(e.target.value)} style={{ ...selectStyle, minWidth: '140px' }}>
                  <option value="">All senders</option>
                  <option value="Rahul Nair">Rahul Nair</option>
                  <option value="Sneha Kapoor">Sneha Kapoor</option>
                  <option value="Arjun Mehta">Arjun Mehta</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as TypeFilter)} style={selectStyle}>
                  <option value="all">Type: All</option>
                  <option value="pdf">PDF</option>
                  <option value="ppt">Presentations</option>
                  <option value="video">Videos</option>
                  <option value="image">Images</option>
                  <option value="xls">Spreadsheets</option>
                </select>
                <select value={readFilter} onChange={e => setReadFilter(e.target.value as ReadFilter)} style={selectStyle}>
                  <option value="">Read: All</option>
                  <option value="unread">Unread</option>
                  <option value="partial">⏳ Partial</option>
                  <option value="read">Fully read</option>
                </select>
                <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
                {(search || typeFilter !== 'all' || readFilter || senderFilter) && (
                  <button onClick={() => { setSearch(''); setTypeFilter('all'); setReadFilter(''); setSenderFilter(''); }} style={{ fontSize: '11px', color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Clear all</button>
                )}
              </div>
            </div>

            {/* Cards grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(165px,1fr))', gap: '14px' }}>
              {filtered.map(coll => (
                <CollateralCard
                  key={coll.id} coll={coll} selected={sel.includes(coll._id || String(coll.id))}
                  onToggle={toggleCard}
                  onPreview={() => setPreviewModal({ coll, page: 0 })}
                  onAnalytics={() => toast(`Opening analytics for ${coll.name}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Playbooks panel */}
        {tab === 'playbooks' && <PlaybooksPanel />}
      </div>

      {/* Preview modal */}
      {previewModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,27,45,.52)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={() => setPreviewModal(null)}>
          <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '560px', boxShadow: '0 20px 60px rgba(0,0,0,.25)', overflow: 'hidden', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--navy)' }}>{previewModal.coll.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text3)' }}>{previewModal.coll.size} · {previewModal.coll.pages} page{previewModal.coll.pages !== 1 ? 's' : ''}</div>
              </div>
              <button onClick={() => setPreviewModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: '20px' }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              {previewModal.coll.type === 'url' ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <i className="ti ti-world-www" style={{ fontSize: '48px', color: '#22C55E', marginBottom: '12px', display: 'block' }} />
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginBottom: '6px' }}>{previewModal.coll.name}</div>
                  <a href={previewModal.coll.url} target="_blank" rel="noopener noreferrer" style={{ color: '#C8973A', fontSize: '13px' }}>{previewModal.coll.url}</a>
                  {previewModal.coll.demoNote && <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text3)', padding: '10px 14px', background: 'var(--surface)', borderRadius: '8px' }}>{previewModal.coll.demoNote}</div>}
                </div>
              ) : (
                <div>
                  {(previewModal.coll.pdata || []).map((p, i) => (
                    <div key={i} style={{ marginBottom: '16px', padding: '16px', background: i === previewModal.page ? '#FDF4E7' : 'var(--surface)', borderRadius: '10px', border: i === previewModal.page ? '2px solid #C8973A' : '1.5px solid transparent', cursor: 'pointer' }} onClick={() => setPreviewModal(prev => prev ? { ...prev, page: i } : null)}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#C8973A', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '.05em' }}>Page {i + 1}</div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)', marginBottom: '3px' }}>{p.t}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text3)', marginBottom: '6px' }}>{p.s}</div>
                      <div style={{ fontSize: '13px', color: 'var(--text2)' }}>{p.b}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
